import React, { useState, useEffect } from 'react'
import { useSelector } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import toast from 'react-hot-toast'
import { 
  UserIcon,
  CameraIcon,
  LinkIcon,
  CodeBracketIcon,
  StarIcon,
  CheckCircleIcon,
  PencilIcon,
  ArrowLeftIcon,
  TrashIcon
} from '@heroicons/react/24/outline'
import { profileAPI, ProfileData, ProfileCompletion } from '../services/profileAPI'

interface LocalProfileState {
  profilePicture: File | null
  profilePictureUrl: string
  socialMedia: {
    linkedin: string
    twitter: string
    instagram: string
    facebook: string
  }
  codingProfiles: {
    leetcode: string
    hackerrank: string
    codeforces: string
    codechef: string
  }
  openSource: {
    github: string
    gitlab: string
    bitbucket: string
  }
  githubProfile: string
  personalInfo: {
    bio: string
    location: string
    website: string
    phone: string
    resume: string
  }
}

const ProfilePage: React.FC = () => {
  const navigate = useNavigate()
  const { user } = useSelector((state: any) => state.auth)
  const [profileData, setProfileData] = useState<LocalProfileState>({
    profilePicture: null,
    profilePictureUrl: '',
    socialMedia: {
      linkedin: '',
      twitter: '',
      instagram: '',
      facebook: ''
    },
    codingProfiles: {
      leetcode: '',
      hackerrank: '',
      codeforces: '',
      codechef: ''
    },
    openSource: {
      github: '',
      gitlab: '',
      bitbucket: ''
    },
    githubProfile: '',
    personalInfo: {
      bio: '',
      location: '',
      website: '',
      phone: '',
      resume: ''
    }
  })
  const [isEditing, setIsEditing] = useState(false)
  const [profileCompletion, setProfileCompletion] = useState(0)
  const [isLoading, setIsLoading] = useState(true)

  // Load profile data from API
  useEffect(() => {
    const loadProfile = async () => {
      if (!user?.id) return
      
      try {
        setIsLoading(true)
        const apiProfile = await profileAPI.getProfile()
        
        // Convert API profile to local state format
        setProfileData({
          profilePicture: null,
          profilePictureUrl: apiProfile.profile_picture_url || '',
          socialMedia: {
            linkedin: apiProfile.linkedin_url || '',
            twitter: apiProfile.twitter_url || '',
            instagram: apiProfile.instagram_url || '',
            facebook: apiProfile.facebook_url || ''
          },
          codingProfiles: {
            leetcode: apiProfile.leetcode_url || '',
            hackerrank: apiProfile.hackerrank_url || '',
            codeforces: apiProfile.codeforces_url || '',
            codechef: apiProfile.codechef_url || ''
          },
          openSource: {
            github: apiProfile.github_url || '',
            gitlab: apiProfile.gitlab_url || '',
            bitbucket: apiProfile.bitbucket_url || ''
          },
          githubProfile: apiProfile.github_url || '',
          personalInfo: {
            bio: apiProfile.bio || '',
            location: apiProfile.location || '',
            website: apiProfile.website || '',
            phone: apiProfile.phone || '',
            resume: apiProfile.resume_url || ''
          }
        })
      } catch (error) {
        console.error('Failed to load profile:', error)
        // Profile doesn't exist yet, keep default state
      } finally {
        setIsLoading(false)
      }
    }
    
    loadProfile()
  }, [user])

  // Load profile completion from API
  useEffect(() => {
    const loadCompletion = async () => {
      if (!user?.id) return
      
      try {
        const completion = await profileAPI.getProfileCompletion()
        setProfileCompletion(completion.completion_percentage)
      } catch (error) {
        console.error('Failed to load profile completion:', error)
        // Calculate locally as fallback
        let completed = 0
        let total = 0

        // Profile picture
        total++
        if (profileData.profilePictureUrl || profileData.profilePicture) completed++

        // Personal info
        Object.values(profileData.personalInfo).forEach(value => {
          total++
          if (value && value.trim()) completed++
        })

        // Social media
        Object.values(profileData.socialMedia).forEach(value => {
          total++
          if (value && value.trim()) completed++
        })

        // Coding profiles
        Object.values(profileData.codingProfiles).forEach(value => {
          total++
          if (value && value.trim()) completed++
        })

        // Open source
        Object.values(profileData.openSource).forEach(value => {
          total++
          if (value && value.trim()) completed++
        })

        // GitHub profile
        total++
        if (profileData.githubProfile && profileData.githubProfile.trim()) completed++

        const percentage = Math.round((completed / total) * 100)
        setProfileCompletion(percentage)
      }
    }
    
    if (!isLoading) {
      loadCompletion()
    }
  }, [user, isLoading, profileData])

  const handleInputChange = (section: keyof LocalProfileState, field: string, value: string) => {
    setProfileData(prev => ({
      ...prev,
      [section]: {
        ...prev[section] as any,
        [field]: value
      }
    }))
  }

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (file) {
      if (file.size > 5 * 1024 * 1024) { // 5MB limit
        toast.error('File size must be less than 5MB')
        return
      }
      if (!file.type.startsWith('image/')) {
        toast.error('Please select an image file')
        return
      }
      
      try {
        // Upload file to API
        const response = await profileAPI.uploadProfilePicture(file)
        
        // Update local state
        setProfileData(prev => ({
          ...prev,
          profilePicture: file,
          profilePictureUrl: response.profile_picture_url
        }))
        
        // Dispatch custom event to notify navbar of profile picture update
        window.dispatchEvent(new CustomEvent('profileUpdated'))
        
        toast.success('Profile picture uploaded successfully!')
      } catch (error: any) {
        console.error('Failed to upload profile picture:', error)
        toast.error(error.message || 'Failed to upload profile picture')
      }
    }
  }

  const handleDeleteProfilePicture = async () => {
    if (!profileData.profilePictureUrl) {
      toast.error('No profile picture to delete')
      return
    }

    if (!window.confirm('Are you sure you want to delete your profile picture?')) {
      return
    }

    try {
      await profileAPI.deleteProfilePicture()
      
      // Update local state
      setProfileData(prev => ({
        ...prev,
        profilePicture: null,
        profilePictureUrl: ''
      }))
      
      // Dispatch custom event to notify navbar of profile picture update
      window.dispatchEvent(new CustomEvent('profileUpdated'))
      
      toast.success('Profile picture deleted successfully!')
    } catch (error: any) {
      console.error('Failed to delete profile picture:', error)
      toast.error(error.message || 'Failed to delete profile picture')
    }
  }

  const handleSave = async () => {
    try {
      // Convert local state to API format
      const apiProfileData: ProfileData = {
        bio: profileData.personalInfo.bio,
        location: profileData.personalInfo.location,
        phone: profileData.personalInfo.phone,
        website: profileData.personalInfo.website || undefined,
        resume_url: profileData.personalInfo.resume || undefined,
        linkedin_url: profileData.socialMedia.linkedin || undefined,
        twitter_url: profileData.socialMedia.twitter || undefined,
        instagram_url: profileData.socialMedia.instagram || undefined,
        facebook_url: profileData.socialMedia.facebook || undefined,
        leetcode_url: profileData.codingProfiles.leetcode || undefined,
        hackerrank_url: profileData.codingProfiles.hackerrank || undefined,
        codeforces_url: profileData.codingProfiles.codeforces || undefined,
        codechef_url: profileData.codingProfiles.codechef || undefined,
        github_url: profileData.openSource.github || undefined,
        gitlab_url: profileData.openSource.gitlab || undefined,
        bitbucket_url: profileData.openSource.bitbucket || undefined
      }
      
      // Save to API
      await profileAPI.updateProfile(apiProfileData)
      
      // Dispatch custom event to notify navbar of profile update
      window.dispatchEvent(new CustomEvent('profileUpdated'))
      
      toast.success('Profile updated successfully!', {
        icon: '✅',
        style: {
          borderRadius: '10px',
          background: '#10B981',
          color: '#fff',
        },
      })
      setIsEditing(false)
    } catch (error: any) {
      console.error('Failed to save profile:', error)
      toast.error(error.message || 'Failed to save profile')
    }
  }

  const handleCancel = async () => {
    try {
      // Reload profile from API
      const apiProfile = await profileAPI.getProfile()
      
      // Convert API profile to local state format
      setProfileData({
        profilePicture: null,
        profilePictureUrl: apiProfile.profile_picture_url || '',
        socialMedia: {
          linkedin: apiProfile.linkedin_url || '',
          twitter: apiProfile.twitter_url || '',
          instagram: apiProfile.instagram_url || '',
          facebook: apiProfile.facebook_url || ''
        },
        codingProfiles: {
          leetcode: apiProfile.leetcode_url || '',
          hackerrank: apiProfile.hackerrank_url || '',
          codeforces: apiProfile.codeforces_url || '',
          codechef: apiProfile.codechef_url || ''
        },
        openSource: {
          github: apiProfile.github_url || '',
          gitlab: apiProfile.gitlab_url || '',
          bitbucket: apiProfile.bitbucket_url || ''
        },
        githubProfile: apiProfile.github_url || '',
        personalInfo: {
          bio: apiProfile.bio || '',
          location: apiProfile.location || '',
          website: apiProfile.website || '',
          phone: apiProfile.phone || '',
          resume: apiProfile.resume_url || ''
        }
      })
    } catch (error) {
      console.error('Failed to reload profile:', error)
      // Keep current state if API fails
    }
    setIsEditing(false)
  }

  const ProfileCompletionCircle = () => {
    const radius = 40
    const strokeWidth = 6
    const normalizedRadius = radius - strokeWidth * 2
    const circumference = normalizedRadius * 2 * Math.PI
    const strokeDasharray = `${circumference} ${circumference}`
    const strokeDashoffset = circumference - (profileCompletion / 100) * circumference

    const getColor = () => {
      if (profileCompletion < 30) return '#EF4444' // red
      if (profileCompletion < 70) return '#F59E0B' // yellow
      return '#10B981' // green
    }

    return (
      <div className="relative w-24 h-24 flex items-center justify-center">
        <svg
          height={radius * 2}
          width={radius * 2}
          className="absolute transform -rotate-90"
        >
          <circle
            stroke="#E5E7EB"
            fill="transparent"
            strokeWidth={strokeWidth}
            r={normalizedRadius}
            cx={radius}
            cy={radius}
          />
          <circle
            stroke={getColor()}
            fill="transparent"
            strokeWidth={strokeWidth}
            strokeDasharray={strokeDasharray}
            style={{ strokeDashoffset }}
            r={normalizedRadius}
            cx={radius}
            cy={radius}
          />
        </svg>
        <span className="text-sm font-bold text-gray-900 relative z-10">
          {profileCompletion}%
        </span>
      </div>
    )
  }

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-indigo-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading profile...</p>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-indigo-50">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <button
            onClick={() => navigate('/dashboard')}
            className="flex items-center space-x-2 text-gray-600 hover:text-gray-900 mb-4 transition-colors"
          >
            <ArrowLeftIcon className="w-5 h-5" />
            <span>Back to Dashboard</span>
          </button>
          
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-4xl font-bold bg-gradient-to-r from-blue-600 to-indigo-600 bg-clip-text text-transparent">
                Profile Settings
              </h1>
              <p className="text-gray-600 mt-2">
                Complete your profile to showcase your skills and achievements
              </p>
            </div>
            <div className="flex items-center space-x-4">
              <ProfileCompletionCircle />
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Profile Picture Section */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-2xl shadow-xl border border-gray-100 p-8">
              <h2 className="text-xl font-bold text-gray-900 mb-6 flex items-center">
                <UserIcon className="w-6 h-6 mr-2 text-blue-600" />
                Profile Picture
              </h2>
              
              <div className="text-center">
                <div className="relative inline-block">
                  <div className="w-32 h-32 rounded-full bg-gray-100 flex items-center justify-center overflow-hidden border-4 border-gray-200">
                    {profileData.profilePictureUrl ? (
                      <img
                        src={profileData.profilePictureUrl}
                        alt="Profile"
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <UserIcon className="w-16 h-16 text-gray-400" />
                    )}
                  </div>
                  {isEditing && (
                    <label className="absolute bottom-0 right-0 bg-blue-600 text-white rounded-full p-2 cursor-pointer hover:bg-blue-700 transition-colors">
                      <CameraIcon className="w-4 h-4" />
                      <input
                        type="file"
                        accept="image/*"
                        onChange={handleFileChange}
                        className="hidden"
                      />
                    </label>
                  )}
                  {/* Delete Profile Picture Button */}
                  {profileData.profilePictureUrl && isEditing && (
                    <button
                      onClick={handleDeleteProfilePicture}
                      className="absolute top-0 right-0 bg-red-600 text-white rounded-full p-2 cursor-pointer hover:bg-red-700 transition-colors"
                      title="Delete profile picture"
                    >
                      <TrashIcon className="w-4 h-4" />
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Profile Form */}
          <div className="lg:col-span-2 space-y-8">
            {/* Personal Information */}
            <div className="bg-white rounded-2xl shadow-xl border border-gray-100 p-8">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-bold text-gray-900 flex items-center">
                  <UserIcon className="w-6 h-6 mr-2 text-green-600" />
                  Personal Information
                </h2>
                {!isEditing && (
                  <button
                    onClick={() => setIsEditing(true)}
                    className="flex items-center space-x-2 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
                  >
                    <PencilIcon className="w-4 h-4" />
                    <span>Edit</span>
                  </button>
                )}
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Bio
                  </label>
                  <textarea
                    value={profileData.personalInfo.bio}
                    onChange={(e) => handleInputChange('personalInfo', 'bio', e.target.value)}
                    disabled={!isEditing}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-50 disabled:cursor-not-allowed"
                    rows={3}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Location
                  </label>
                  <input
                    type="text"
                    value={profileData.personalInfo.location}
                    onChange={(e) => handleInputChange('personalInfo', 'location', e.target.value)}
                    disabled={!isEditing}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-50 disabled:cursor-not-allowed"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Phone
                  </label>
                  <input
                    type="tel"
                    value={profileData.personalInfo.phone}
                    onChange={(e) => handleInputChange('personalInfo', 'phone', e.target.value)}
                    disabled={!isEditing}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-50 disabled:cursor-not-allowed"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Website/Portfolio
                  </label>
                  <input
                    type="url"
                    value={profileData.personalInfo.website}
                    onChange={(e) => handleInputChange('personalInfo', 'website', e.target.value)}
                    disabled={!isEditing}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-50 disabled:cursor-not-allowed"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Resume Link
                  </label>
                  <input
                    type="url"
                    value={profileData.personalInfo.resume}
                    onChange={(e) => handleInputChange('personalInfo', 'resume', e.target.value)}
                    disabled={!isEditing}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-50 disabled:cursor-not-allowed"
                  />
                </div>
              </div>
            </div>

            {/* Social Media Links */}
            <div className="bg-white rounded-2xl shadow-xl border border-gray-100 p-8">
              <h2 className="text-xl font-bold text-gray-900 mb-6 flex items-center">
                <LinkIcon className="w-6 h-6 mr-2 text-purple-600" />
                Social Media Links
              </h2>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    LinkedIn
                  </label>
                  <input
                    type="url"
                    value={profileData.socialMedia.linkedin}
                    onChange={(e) => handleInputChange('socialMedia', 'linkedin', e.target.value)}
                    disabled={!isEditing}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-50 disabled:cursor-not-allowed"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Twitter
                  </label>
                  <input
                    type="url"
                    value={profileData.socialMedia.twitter}
                    onChange={(e) => handleInputChange('socialMedia', 'twitter', e.target.value)}
                    disabled={!isEditing}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-50 disabled:cursor-not-allowed"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Instagram
                  </label>
                  <input
                    type="url"
                    value={profileData.socialMedia.instagram}
                    onChange={(e) => handleInputChange('socialMedia', 'instagram', e.target.value)}
                    disabled={!isEditing}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-50 disabled:cursor-not-allowed"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Facebook
                  </label>
                  <input
                    type="url"
                    value={profileData.socialMedia.facebook}
                    onChange={(e) => handleInputChange('socialMedia', 'facebook', e.target.value)}
                    disabled={!isEditing}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-50 disabled:cursor-not-allowed"
                  />
                </div>
              </div>
            </div>

            {/* Coding Profiles */}
            <div className="bg-white rounded-2xl shadow-xl border border-gray-100 p-8">
              <h2 className="text-xl font-bold text-gray-900 mb-6 flex items-center">
                <CodeBracketIcon className="w-6 h-6 mr-2 text-orange-600" />
                Coding Profiles
              </h2>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    LeetCode
                  </label>
                  <input
                    type="url"
                    value={profileData.codingProfiles.leetcode}
                    onChange={(e) => handleInputChange('codingProfiles', 'leetcode', e.target.value)}
                    disabled={!isEditing}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-50 disabled:cursor-not-allowed"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    HackerRank
                  </label>
                  <input
                    type="url"
                    value={profileData.codingProfiles.hackerrank}
                    onChange={(e) => handleInputChange('codingProfiles', 'hackerrank', e.target.value)}
                    disabled={!isEditing}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-50 disabled:cursor-not-allowed"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Codeforces
                  </label>
                  <input
                    type="url"
                    value={profileData.codingProfiles.codeforces}
                    onChange={(e) => handleInputChange('codingProfiles', 'codeforces', e.target.value)}
                    disabled={!isEditing}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-50 disabled:cursor-not-allowed"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    CodeChef
                  </label>
                  <input
                    type="url"
                    value={profileData.codingProfiles.codechef}
                    onChange={(e) => handleInputChange('codingProfiles', 'codechef', e.target.value)}
                    disabled={!isEditing}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-50 disabled:cursor-not-allowed"
                  />
                </div>
              </div>
            </div>

            {/* Open Source Contributions */}
            <div className="bg-white rounded-2xl shadow-xl border border-gray-100 p-8">
              <h2 className="text-xl font-bold text-gray-900 mb-6 flex items-center">
                <StarIcon className="w-6 h-6 mr-2 text-yellow-600" />
                Open Source Contributions
              </h2>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    GitHub
                  </label>
                  <input
                    type="url"
                    value={profileData.openSource.github}
                    onChange={(e) => handleInputChange('openSource', 'github', e.target.value)}
                    disabled={!isEditing}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-50 disabled:cursor-not-allowed"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    GitLab
                  </label>
                  <input
                    type="url"
                    value={profileData.openSource.gitlab}
                    onChange={(e) => handleInputChange('openSource', 'gitlab', e.target.value)}
                    disabled={!isEditing}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-50 disabled:cursor-not-allowed"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Bitbucket
                  </label>
                  <input
                    type="url"
                    value={profileData.openSource.bitbucket}
                    onChange={(e) => handleInputChange('openSource', 'bitbucket', e.target.value)}
                    disabled={!isEditing}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-50 disabled:cursor-not-allowed"
                  />
                </div>
              </div>
            </div>

            {/* GitHub Profile */}
            <div className="bg-white rounded-2xl shadow-xl border border-gray-100 p-8">
              <h2 className="text-xl font-bold text-gray-900 mb-6 flex items-center">
                <CodeBracketIcon className="w-6 h-6 mr-2 text-gray-800" />
                GitHub Profile
              </h2>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Main GitHub Profile
                </label>
                <input
                  type="url"
                  value={profileData.githubProfile}
                  onChange={(e) => setProfileData(prev => ({ ...prev, githubProfile: e.target.value }))}
                  disabled={!isEditing}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-50 disabled:cursor-not-allowed"
                />
                <p className="text-sm text-gray-500 mt-2">
                  Your main GitHub profile for showcasing repositories and contributions
                </p>
              </div>
            </div>

            {/* Action Buttons */}
            {isEditing && (
              <div className="bg-white rounded-2xl shadow-xl border border-gray-100 p-8">
                <div className="flex items-center justify-end space-x-4">
                  <button
                    onClick={handleCancel}
                    className="px-6 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleSave}
                    className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center space-x-2"
                  >
                    <CheckCircleIcon className="w-5 h-5" />
                    <span>Save Changes</span>
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

export default ProfilePage
